<template>
    <div class="w-full flex gap-5 justify-center">
        <div class="w-3/4 flex flex-col">
            <div class="flex flex-col gap-3 items-start mb-3">
                <div class="font-medium">Select a Transform Type</div>
                <SearchInput
                    :custom-class="'w-2/3'"
                    :placeholder="`Search for a transform type...`"
                    @search="(input) => (transformSearchQuery = input)" />
            </div>

            <div class="group flex flex-col items-start gap-3 overflow-y-auto pb-3 pr-3" style="max-height: 82vh">
                <div v-for="(group, groupName) in filteredTransformGroups" :key="groupName" class="relative w-full">
                    <div
                        :class="[
                            'w-full relative rounded-lg p-4 border shadow-wrapper flex flex-col gap-3 transition-all duration-300 ease-in-out',
                            group.length === 1 && group[0].coming_soon
                                ? 'bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed pointer-events-none'
                                : 'bg-white cursor-pointer hover:bg-gray-50'
                        ]"
                        @click="!(group.length === 1 && group[0].coming_soon) && (group.length > 1 ? selectGroup(groupName) : selectItem(group[0]))">
                        <div class="flex justify-between items-center">
                            <div class="flex items-center gap-4 font-medium">
                                <div class="min-w-[80px]">
                                    <img
                                        :src="getTransformIcon(groupName)"
                                        alt=""
                                        class="h-10"
                                        :style="{
                                            width: '60px',
                                            objectFit: 'contain',
                                            objectPosition: 'left center'
                                        }" />
                                </div>
                                <div class="flex flex-col">
                                    <div class="flex items-center gap-2">
                                        <span class="font-medium text-gray-900">{{ groupName }}</span>
                                        <span
                                            v-if="group.length === 1 && group[0].coming_soon"
                                            class="inline-flex items-center rounded-full px-3 py-1 text-xs bg-orange-300 text-gray-800 shadow font-medium">
                                            Coming soon
                                        </span>
                                    </div>
                                    <div
                                        v-if="filteredTransformGroups[groupName].find((x) => x.display_name === group[0].display_name)?.description"
                                        class="mt-1 text-sm text-gray-500">
                                        {{ filteredTransformGroups[groupName].find((x) => x.display_name === group[0].display_name)?.description }}
                                    </div>
                                </div>
                            </div>
                            <!-- <div class="flex flex-row items-center justify-center h-full flex-wrap rounded py-0.5 text-lg font-medium">
                                <span
                                    class="inline-flex items-center rounded-full px-3 py-1 text-xs bg-green-200 text-gray-800 shadow font-medium"
                                    title="Transforms"
                                    >{{ group.length }} type{{ group.length > 1 ? 's' : '' }}</span
                                >
                            </div> -->
                        </div>
                        <div
                            :style="{
                                'max-height': selectedGroupName === groupName ? '10000px' : '0px',
                                transition: selectedGroupName === groupName ? 'max-height 0.5s ease-in-out' : ''
                            }"
                            class="overflow-hidden">
                            <!-- Vertical transform line for the parent -->
                            <div v-if="selectedGroupName === groupName" class="node-branch"></div>
                            <div
                                v-for="(child, index) in group"
                                :key="index"
                                class="flex flex-col w-full items-start gap-3 mt-3 child-node-container">
                                <!-- Horizontal leaf for each child -->
                                <div class="node-branch-leaf"></div>
                                <div
                                    :class="[
                                        'w-full rounded-lg transition-all duration-300 ease-in-out ml-8 p-4',
                                        child.coming_soon
                                            ? 'bg-gray-100 border border-gray-200 opacity-50 cursor-not-allowed pointer-events-none'
                                            : 'bg-white border hover:bg-gray-50 cursor-pointer'
                                    ]"
                                    @click.stop="!child.coming_soon && selectItem(child)">
                                    <div class="flex items-start gap-3">
                                        <div class="min-w-[80px]">
                                            <img
                                                :src="getTransformIcon(child.transform)"
                                                alt=""
                                                class="h-10"
                                                :style="{
                                                    width: '60px',
                                                    objectFit: 'contain',
                                                    objectPosition: 'left center'
                                                }" />
                                        </div>
                                        <div class="flex flex-col">
                                            <div class="flex items-center gap-2">
                                                <span class="font-medium text-gray-900">{{ child.display_name }}</span>
                                                <span
                                                    v-if="child.coming_soon"
                                                    class="inline-flex items-center rounded-full px-3 py-1 text-xs bg-orange-300 text-gray-800 shadow font-medium">
                                                    Coming soon
                                                </span>
                                            </div>
                                            <div v-if="child.description" class="mt-1 text-sm text-gray-500">{{ child.description }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="!filteredTransformGroups || !Object.keys(filteredTransformGroups).length" class="text-center text-gray-500">
                    No {{ props.type?.toLowerCase() }} found
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { getTransformIcon } from '@/utils/constants';
import SearchInput from '@/components/global/SearchInput.vue';

const props = defineProps({
    transformGroups: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['select-transform']);

let transformSearchQuery = ref('');

const filteredTransformGroups = computed(() => {
    if (transformSearchQuery.value) {
        const q = transformSearchQuery.value.toLowerCase();
        return Object.keys(props.transformGroups).reduce((acc, groupName) => {
            const filteredGroup = props.transformGroups[groupName].filter(
                (x) => x.display_name.toLowerCase().includes(q) || (x.description && x.description.toLowerCase().includes(q))
            );
            if (filteredGroup.length) {
                acc[groupName] = filteredGroup;
            }
            return acc;
        }, {});
    }

    return props.transformGroups;
});

const selectedGroupName = ref(null);

const selectGroup = (groupName) => {
    selectedGroupName.value = selectedGroupName.value === groupName ? null : groupName;
};

const selectItem = (child) => {
    emit('select-transform', child);
};
</script>

<style>
.tile:hover {
    transform: translateY(-2px); /* Reduced movement */
}

.group-item {
    transition:
        transform 0.3s ease,
        box-shadow 0.3s ease; /* Smooth transition for hover effects */
}

.group-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2); /* Slightly more noticeable shadow for testing */
}

.shadow-wrapper::before,
.shadow-wrapper::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 10px;
    pointer-events: none;
}

.node-branch {
    position: absolute;
    top: 0px; /* Adjust as needed to align with the parent's title */
    bottom: 0;
    left: 15px; /* Adjust to align with the center of the parent's icon */
    width: 2px; /* Width of the vertical line */
    background-color: #efefef;
}

.node-branch-leaf {
    position: absolute;
    top: 50%;
    left: 0px; /* Same as the left position of the vertical line */
    width: 20px; /* Length of the horizontal line */
    height: 2px; /* Thickness of the line */
    background-color: #efefef;
    transform: translateY(-50%);
}

.child-node-container {
    display: flex;
    position: relative;
    align-items: flex-start;
    width: 100%;
}
</style>
